import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { ArrowBack, Search } from "@mui/icons-material";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import EmptyState from "../../lib/components/EmptyState";
import AppTextField from "../../lib/components/AppTextField";
import AppTableLoader from "../../lib/components/api/AppTableLoader";
import CustomPagination from "../../lib/components/AppPagination";
import { RequestMethodsBadge } from "../../lib/components/api/ResponseStatusBadge";
import { apiTransmissionLogPath } from "../../constants/pathnames";
import useDebounce from "../../hooks/useDebounce";

import transmissionService from "../../service/transmissionService";
import { DATE_PRESETS, extractFilterDates, normalizeDatePreset } from "./components/DateRangePreset";
import FilterLogsModal, {
  ALL_LOG_METHODS,
  ALL_STATUS_CLASSES,
  toLogQueryParams,
} from "./components/FilterLogsModal";

const StatusCodeBadge = ({ code }) => {
  const value = Number(code);
  const success = value >= 200 && value < 300;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 48,
        px: 1.4,
        py: 0.35,
        borderRadius: "999px",
        bgcolor: success ? "#ECFDF5" : "#FEF2F2",
        color: success ? "#059669" : "#EF4444",
        border: `1px solid ${success ? "#6EE7B7" : "#FCA5A5"}`,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {value || "\u2014"}
    </Box>
  );
};

function parseSingleEnum(value, allowed) {
  if (!value) return null;
  const normalized = String(value).trim();
  return allowed.includes(normalized) ? normalized : null;
}

function writeLogFiltersToParams(params, filters) {
  const query = toLogQueryParams(filters);
  ["datePreset", "dateFrom", "dateTo", "status", "failureCategory", "method", "statusClass"].forEach((key) => {
    params.delete(key);
  });

  if (query.datePreset) params.set("datePreset", query.datePreset);
  if (query.dateFrom) params.set("dateFrom", query.dateFrom);
  if (query.dateTo) params.set("dateTo", query.dateTo);
  if (query.status) params.set("status", query.status);
  if (query.failureCategory) params.set("failureCategory", query.failureCategory);
  if (query.method) params.set("method", query.method);
  if (query.statusClass) params.set("statusClass", query.statusClass);
}

const APILogs = () => {
  const { id: organizationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const page = searchParams.get("page") ?? "1";
  const limit = searchParams.get("limit") ?? "20";
  const keyword = searchParams.get("search") || "";
  const searchTerm = useDebounce(keyword, 800);
  const [responseFilters, setResponseFilters] = React.useState(null);

  const urlDatePreset = searchParams.get("datePreset");
  const logFilters = {
    datePreset: normalizeDatePreset(urlDatePreset || responseFilters?.datePreset || DATE_PRESETS.THIS_MONTH),
    dateFrom: searchParams.get("dateFrom") || responseFilters?.dateFrom || null,
    dateTo: searchParams.get("dateTo") || responseFilters?.dateTo || null,
    status: searchParams.get("status") || null,
    failureCategory: searchParams.get("failureCategory") || null,
    method: parseSingleEnum(searchParams.get("method"), ALL_LOG_METHODS),
    statusClass: parseSingleEnum(searchParams.get("statusClass"), ALL_STATUS_CLASSES),
  };
  const logQueryParams = toLogQueryParams(logFilters);

  const logsQuery = useQuery({
    enabled: !!organizationId,
    queryKey: ["api-request-logs", organizationId, page, limit, searchTerm, logQueryParams],
    queryFn: () =>
      transmissionService.getApiRequestLogs({
        organizationId,
        page,
        limit,
        search: searchTerm || undefined,
        ...logQueryParams,
      }),
  });

  React.useEffect(() => {
    const filters = logsQuery.data?.data?.filters;
    if (!filters) return;
    setResponseFilters(extractFilterDates(filters));
  }, [logsQuery.data?.data?.filters]);

  const updateSearchParams = React.useCallback(
    (setter) => {
      const params = new URLSearchParams(location.search);
      setter(params);
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  const handleChangeQuery = React.useCallback(
    (name, value) => {
      updateSearchParams((params) => {
        if (value) params.set(name, value);
        else params.delete(name);
      });
    },
    [updateSearchParams]
  );

  const handleKeywordChange = (event) => {
    updateSearchParams((params) => {
      const value = event.target.value;
      if (value) params.set("search", value);
      else params.delete("search");
      params.set("page", "1");
    });
  };

  const handleApplyFilters = (nextFilters) => {
    updateSearchParams((params) => {
      writeLogFiltersToParams(params, nextFilters);
      params.set("page", "1");
    });
  };

  const result = logsQuery?.data?.data;

  return (
    <AppLayout>
      <Header
        title="API Logs"
        description={`Last ${result?.totalDocs || 0} requests (refreshed every 5 minutes)`}
      />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton className="bg-secondary-subtle mb-3" onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>

        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={3}>
            <Typography fontSize={16} fontWeight={500}>
              API Logs
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box sx={{ width: { xs: "100%", sm: 260 } }}>
                <AppTextField
                  placeholder="Search logs..."
                  value={keyword}
                  onChange={handleKeywordChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      borderRadius: "10px",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "#9CA3AF", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <FilterLogsModal
                filters={logFilters}
                defaultDatePreset={DATE_PRESETS.THIS_MONTH}
                onApply={handleApplyFilters}
              />
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F6F6F6" }}>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>S/N</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <AppTableLoader
                  cols={7}
                  items={result?.data}
                  isLoading={logsQuery.isPending}
                />

                {result?.data?.map((item, index) => (
                  <TableRow key={item?.id}>
                    <TableCell>
                      {(Math.max(1, parseInt(page, 10) || 1) - 1) * Math.max(1, parseInt(limit, 10) || 20) + index + 1}
                    </TableCell>
                    <TableCell>
                      {moment(item?.createdAt).format("DD-MM-YYYY LT")}
                    </TableCell>

                    <TableCell>
                      <RequestMethodsBadge method={item?.method} />
                    </TableCell>

                    <TableCell sx={{ maxWidth: 500 }}>
                      <Typography noWrap>
                        {item?.endpoint}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <StatusCodeBadge code={item?.statusCode} />
                    </TableCell>

                    <TableCell>
                      {item?.durationMs} ms
                    </TableCell>

                    <TableCell>
                      <Button
                        component={Link}
                        to={apiTransmissionLogPath(organizationId, item?.id)}
                        state={{ log: item }}
                        variant="text"
                        color="primary"
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <EmptyState isEmpty={!logsQuery.isPending && !result?.data?.length} />
        </Paper>
        <CustomPagination
          page={Math.max(1, parseInt(page, 10) || 1)}
          count={result?.totalPages || 1}
          limit={Number(limit)}
          onPageChange={(e, value) => handleChangeQuery("page", value.toString())}
          onLimitChange={(newLimit) => {
            updateSearchParams((params) => {
              params.set("page", "1");
              params.set("limit", newLimit.toString());
            });
          }}
        />
      </div>
    </AppLayout>
  );
};

export default APILogs;
